import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@deskbay/shared/services/supabase/client';
import { SUBSCRIPTION_PRICING, SubscriptionPlanName } from '../types/subscription';
import { logger } from '@deskbay/shared';

// =====================================================
// TYPES
// =====================================================

interface PricingData {
    plan_name: SubscriptionPlanName;
    display_name: string;
    price_cents: number;
    apple_price_cents: number | null;
    price_display: string;
    apple_price_display: string | null;
    duration_days: number;
    discount_percentage: number;
    monthly_equivalent_cents: number;
    stripe_price_id: string | null;
    apple_product_id: string | null;
    is_active: boolean;
}

interface PricingContextValue {
    prices: Record<SubscriptionPlanName, PricingData> | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

// =====================================================
// CONTEXT
// =====================================================

const PricingContext = createContext<PricingContextValue | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface PricingProviderProps {
    children: ReactNode;
}

export function PricingProvider({ children }: PricingProviderProps) {
    const [prices, setPrices] = useState<Record<SubscriptionPlanName, PricingData> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    async function fetchPrices() {
        try {
            setIsLoading(true);
            setError(null);

            const { data, error: rpcError } = await supabase.rpc('get_subscription_prices');

            if (rpcError) {
                throw rpcError;
            }

            if (!data || data.length === 0) {
                throw new Error('No pricing data returned from database');
            }

            // Convert array to Record keyed by plan_name
            const pricesMap = data.reduce((acc, plan) => {
                acc[plan.plan_name as SubscriptionPlanName] = plan as PricingData;
                return acc;
            }, {} as Record<SubscriptionPlanName, PricingData>);

            setPrices(pricesMap);
            logger.stripe.debug('Pricing data loaded from DB', 'PricingContext', { count: data.length });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            logger.stripe.error('Failed to load pricing from DB, using fallback', err, 'PricingContext');
            setError(err instanceof Error ? err : new Error(errorMessage));

            // Fallback: use hardcoded SUBSCRIPTION_PRICING
            const fallbackPrices: Record<SubscriptionPlanName, PricingData> = {
                trial: {
                    plan_name: 'trial',
                    display_name: SUBSCRIPTION_PRICING.trial.display_name,
                    price_cents: SUBSCRIPTION_PRICING.trial.price_cents,
                    apple_price_cents: null,
                    price_display: SUBSCRIPTION_PRICING.trial.price_display,
                    apple_price_display: null,
                    duration_days: SUBSCRIPTION_PRICING.trial.duration_days,
                    discount_percentage: 0,
                    monthly_equivalent_cents: 0,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                monthly: {
                    plan_name: 'monthly',
                    display_name: SUBSCRIPTION_PRICING.monthly.display_name,
                    price_cents: SUBSCRIPTION_PRICING.monthly.price_cents,
                    apple_price_cents: null,
                    price_display: SUBSCRIPTION_PRICING.monthly.price_display,
                    apple_price_display: null,
                    duration_days: SUBSCRIPTION_PRICING.monthly.duration_days,
                    discount_percentage: 0,
                    monthly_equivalent_cents: SUBSCRIPTION_PRICING.monthly.price_cents,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                yearly: {
                    plan_name: 'yearly',
                    display_name: SUBSCRIPTION_PRICING.yearly.display_name,
                    price_cents: SUBSCRIPTION_PRICING.yearly.price_cents,
                    apple_price_cents: null,
                    price_display: SUBSCRIPTION_PRICING.yearly.price_display,
                    apple_price_display: null,
                    duration_days: SUBSCRIPTION_PRICING.yearly.duration_days,
                    discount_percentage: SUBSCRIPTION_PRICING.yearly.discount_percentage,
                    monthly_equivalent_cents: SUBSCRIPTION_PRICING.yearly.monthly_equivalent_cents,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                setup_assisted: {
                    plan_name: 'setup_assisted',
                    display_name: SUBSCRIPTION_PRICING.setup_assisted.display_name,
                    price_cents: SUBSCRIPTION_PRICING.setup_assisted.price_cents,
                    apple_price_cents: null,
                    price_display: SUBSCRIPTION_PRICING.setup_assisted.price_display,
                    apple_price_display: null,
                    duration_days: 0,
                    discount_percentage: 0,
                    monthly_equivalent_cents: 0,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                test_1eur: {
                    plan_name: 'test_1eur',
                    display_name: SUBSCRIPTION_PRICING.test_1eur.display_name,
                    price_cents: SUBSCRIPTION_PRICING.test_1eur.price_cents,
                    apple_price_cents: null,
                    price_display: SUBSCRIPTION_PRICING.test_1eur.price_display,
                    apple_price_display: null,
                    duration_days: 30,
                    discount_percentage: 0,
                    monthly_equivalent_cents: 0,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                unlimited: {
                    plan_name: 'unlimited',
                    display_name: 'Unlimited',
                    price_cents: 0,
                    apple_price_cents: null,
                    price_display: '€0,00',
                    apple_price_display: null,
                    duration_days: 36500,
                    discount_percentage: 0,
                    monthly_equivalent_cents: 0,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
                affiliate: {
                    plan_name: 'affiliate',
                    display_name: 'Affiliate',
                    price_cents: 0,
                    apple_price_cents: null,
                    price_display: '€0,00',
                    apple_price_display: null,
                    duration_days: 36500,
                    discount_percentage: 0,
                    monthly_equivalent_cents: 0,
                    stripe_price_id: null,
                    apple_product_id: null,
                    is_active: true,
                },
            };

            setPrices(fallbackPrices);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchPrices();
    }, []);

    const value: PricingContextValue = {
        prices,
        isLoading,
        error,
        refetch: fetchPrices,
    };

    return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
}

// =====================================================
// HOOK
// =====================================================

export function usePricing(): PricingContextValue {
    const context = useContext(PricingContext);
    if (context === undefined) {
        throw new Error('usePricing must be used within a PricingProvider');
    }
    return context;
}

// =====================================================
// HELPER: Get price for specific plan
// =====================================================

export function usePlanPrice(planName: SubscriptionPlanName): PricingData | null {
    const { prices } = usePricing();
    return prices?.[planName] ?? null;
}
